export enum FilterCategory {
  UNSPECIFIED = 0,
  CLIENT = 1,
  GUILD = 2,
  USER = 3,
  UTILITY = 4,
}

export enum FilterEvaluationMode {
  UNSPECIFIED = 0,
  LAZY = 1,
  STICKY = 2,
}

export enum ExperimentUnitType {
  UNSPECIFIED = 0,
  USER = 1,
  INSTALLATION = 2,
  GUILD = 3,
  CUSTOM = 4,
}

export enum ExperimentType {
  UNSPECIFIED = 0,
  ACTIVE = 1,
  UNUSED = 2,
  BURNED = 3,
  PRESERVED = 4,
}

export enum Phase {
  UNSPECIFIED = 0,
  DRAFT = 1,
  MEASUREMENT = 2,
  ROLLING_OUT = 4,
  ARCHIVED = 6,
  AA_MODE = 7,
}

export enum Surface {
  UNSPECIFIED = 0,
  API = 1,
  APP = 2,
  DEVELOPER_PORTAL = 3,
  ADMIN_PANEL = 4,
  ADS_BUDGET_AB = 5,
  AV_WORKER = 6,
  SEO = 7,
  MARKETING = 8,
}

export enum AssignmentMode {
  FULL = 0,
  FORCE_CONTROL = 3,
  OVERRIDES_ONLY = 4,
  OFF = 5,
}

export enum ExposureTracking {
  ENABLED = 0,
  DISABLED = 1,
}

export enum EligibilityPersistence {
  DEFAULT = 0,
  OFF = 1,
  OVERRIDES_ONLY = 2,
}

export enum RuleSubtype {
  REGULAR = 0,
  HOLDOUT = 1,
}

// ===== Shared primitives =====

export interface Fixed64Value {
  value: string;
}

export interface UInt32Value {
  value: number;
}

export interface UInt64Value {
  value: string;
}

export interface Timestamp {
  seconds: string;
  nanos: number;
}

// ===== Core experiment types =====

export interface Bucket {
  start: number;
  stop: number;
  type: ExperimentType;
}

export interface Variation {
  id: number;
  label: string;
  target_allocation: number;
  buckets: Bucket[];
  type: ExperimentType;
  configuration?: string;
  owning_experiment_id: string;
}

export interface Rule {
  type: ExperimentType;
  filters: Filter[];
  override?: Override;
  is_sunset_rule: boolean;
  subtype: RuleSubtype;
  hash: string;
}

export interface Override {
  variation_id: number;
}

// ===== Filters =====

export type Location =
  | { iso_region: { iso_country: string; iso_subdivision: string } }
  | { is_eu: boolean }
  | { place: { city: string; subdivision: string; country: string } };

export type Filter =
  | { client_version: ClientPlatform; negate?: boolean }
  | { client_os: ClientOperatingSystem; negate?: boolean }
  | { staff: StaffUsers; negate?: boolean }
  | { user_in_guild: UserInGuild; negate?: boolean }
  | { user_ids: UserIds; negate?: boolean }
  | { client_locale: ClientLocale; negate?: boolean }
  | { client_location: ClientLocation; negate?: boolean }
  | { client_ip: ClientIP; negate?: boolean }
  | { user_locale: UserLocale; negate?: boolean }
  | { bot: UserIsBot; negate?: boolean }
  | { user_age_range: UserAgeRange; negate?: boolean }
  | { user_id_range: UserIDRange; negate?: boolean }
  | { user_has_flag: UserHasFlag; negate?: boolean }
  | { unit_id_in_range_by_hash: UnitIdInRangeByHash; negate?: boolean }
  | { client_release_channel: ClientReleaseChannel; negate?: boolean }
  | { always: Always; negate?: boolean }
  | { client_system_locale: ClientSystemLocale; negate?: boolean }
  | { unit_id_in_experiment: UnitIdInExperiment; negate?: boolean }
  | { user_premium_type: UserPremiumType; negate?: boolean }
  | { unit_id_matches_filter_snapshot: UnitIdMatchesFilterSnapshot; negate?: boolean }
  | { guild_ids: GuildIds; negate?: boolean }
  | { guild_id_range: GuildIdRange; negate?: boolean }
  | { guild_member_count_range: GuildMemberCountRange; negate?: boolean }
  | { guild_has_feature: GuildHasFeature; negate?: boolean }
  | { user_location: UserLocation; negate?: boolean }
  | { user_ip: UserIP; negate?: boolean }
  | { installation_ids: InstallationIds; negate?: boolean };

// ===== Client / user / guild filters =====

export interface PlatformVersionSpecifier {
  major: number;
  minor?: number;
  build?: string;
}

export interface PlatformVersionRangeBound {
  version?: PlatformVersionSpecifier;
  inclusive: boolean;
}

export interface PlatformVersionRange {
  lower_bound?: PlatformVersionRangeBound;
  upper_bound?: PlatformVersionRangeBound;
}

export interface PlatformVersion {
  ranges: PlatformVersionRange[];
  work_around_pyoto_bug: boolean;
}

export interface ClientRequiredChanges {
  commit_hashes: string[];
  pr_numbers: number[];
}

export interface ClientPlatform {
  ios_version?: PlatformVersion;
  android_version?: PlatformVersion;
  web_version?: PlatformVersion;
  native_version?: PlatformVersion;
  allow_non_native_web: boolean;
  client_required_changes?: ClientRequiredChanges;
}

export interface SDKVersionSpecifier {
  version: number;
}

export interface SDKVersionRangeBound {
  version?: SDKVersionSpecifier;
  inclusive: boolean;
}

export interface SDKVersionRange {
  lower_bound?: SDKVersionRangeBound;
  upper_bound?: SDKVersionRangeBound;
}

export interface SDKVersion {
  ranges: SDKVersionRange[];
  work_around_pyoto_bug: boolean;
}

export interface ClientOperatingSystem {
  ios_version?: SDKVersion;
  android_version?: SDKVersion;
  macos_version?: SDKVersion;
  windows_version?: SDKVersion;
  playstation_version?: SDKVersion;
  xbox_version?: SDKVersion;
  linux_version?: SDKVersion;
}

export interface StaffUsers {
  work_accounts: boolean;
  personal_accounts: boolean;
}

export interface UserInGuild {
  guild_ids: string[];
}

export interface UserIds {
  user_ids: string[];
}

export interface ClientLocale {
  locales: string[];
}

export interface ISORegion {
  iso_country: string;
  iso_subdivision: string;
}

export interface Place {
  city: string;
  subdivision: string;
  country: string;
}

export interface ClientLocation {
  locations: Location[];
}

export interface ClientIP {
  blocks: string[];
}

export interface UserLocale {
  locales: string[];
}

export interface UserIsBot {
  is_bot: boolean;
}

export interface UserAgeRange {
  min_age_years?: number;
  max_age_years?: number;
}

export interface UserIDRange {
  min_id?: Fixed64Value;
  max_id?: Fixed64Value;
}

export interface UserHasFlag {
  mask: string;
}

export interface UnitIdInRangeByHash {
  hash_key: string;
  stop_ring_position: number;
  start_ring_position: number;
}

export interface ClientReleaseChannel {
  release_channels: string[];
}

export interface Always {
  value: boolean;
}

export interface ClientSystemLocale {
  locales: string[];
}

export interface UnitIdInExperiment {
  experiment_id: string;
  variation_ids: number[];
}

export interface UserPremiumType {
  premium_types: number[];
}

export interface UnitIdMatchesFilterSnapshot {
  filter_snapshot_name: string;
  target_filter_values: string[];
}

export interface GuildIds {
  guild_ids: string[];
}

export interface GuildIdRange {
  min_id?: Fixed64Value;
  max_id?: Fixed64Value;
}

export interface GuildMemberCountRange {
  min_count?: number;
  max_count?: number;
}

export interface GuildHasFeature {
  features: string[];
}

export interface UserLocation {
  locations: Location[];
  prefer_client_ip: boolean;
}

export interface UserIP {
  blocks: string[];
  prefer_client_ip: boolean;
}

export interface InstallationIds {
  installation_ids: string[];
}

// ===== Main Experiment =====

export interface Experiment {
  id: string;
  name: string;
  created_at?: Timestamp;
  creator_id: string;
  version: number;
  edited_at?: Timestamp;
  editor_id: string;
  title: string;
  description: string;
  hypothesis?: string;
  tech_spec_link?: string;
  revision: number;
  hash_key: string;
  unit_type: ExperimentUnitType;
  variations: Variation[];
  rules: Rule[];
  phase: Phase;
  surfaces: Surface[];
  owning_team_id: string;
  cached_notification_channel_id: string;
  exposure_tracking: ExposureTracking;
  assignment_mode: AssignmentMode;
  enable_edit_raw_json_ui: boolean;
  winning_variation_id: number;
  extra_outcome_context: string;
  type: ExperimentType;
  is_template: boolean;
  field_numbers_to_copy: number[];
  engine_feature_flags: string[];
  debug_config?: {
    enable_decision_logging: boolean;
    metrics_sample_rate: number;
    log_context_on_failure: boolean;
    log_raw_headers: boolean;
    tag_filter_metrics: boolean;
    decision_log_sample_rate: number;
  };
  expected_end_date?: Timestamp;
  is_automated_change: boolean;
  suppress_editor_mention: boolean;
  archive_at?: Timestamp;
  guild_experiment_version?: number;
  custom_unit_prefix: number;
  growthbook_tags: string[];
  allocate_right_to_left: boolean;
  is_managed: boolean;
  number_line_settings?: {
    mode: number;
    linked_id: string;
    shared_control: boolean;
  };
  eligibility_persistence: EligibilityPersistence;
}

// ===== Helpers =====

export const FILTER_TYPE_NAMES: Record<string, string> = {
  client_version: "Client Version",
  client_os: "Client OS",
  staff: "Staff",
  user_in_guild: "User in Guild",
  user_ids: "User IDs",
  client_locale: "Client Locale",
  client_location: "Client Location",
  client_ip: "Client IP",
  user_locale: "User Locale",
  bot: "Bot",
  user_age_range: "User Age Range",
  user_id_range: "User ID Range",
  user_has_flag: "User Has Flag",
  unit_id_in_range_by_hash: "Unit ID in Range by Hash",
  client_release_channel: "Client Release Channel",
  always: "Always",
  client_system_locale: "Client System Locale",
  unit_id_in_experiment: "Unit ID in Experiment",
  user_premium_type: "User Premium Type",
  unit_id_matches_filter_snapshot: "Unit ID Matches Filter Snapshot",
  guild_ids: "Guild IDs",
  guild_id_range: "Guild ID Range",
  guild_member_count_range: "Guild Member Count Range",
  guild_has_feature: "Guild Has Feature",
  user_location: "User Location",
  user_ip: "User IP",
  installation_ids: "Installation IDs",
};

export function getFilterTypeName(filter: Filter): string {
  const key = Object.keys(filter).find((k) => k !== "negate");
  return key ? FILTER_TYPE_NAMES[key] ?? key : "Unknown";
}

export function getFilterTypeKey(filter: Filter): string {
  const key = Object.keys(filter).find((k) => k !== "negate");
  return key ?? "unknown";
}

export function createDefaultFilter(type: string): Filter {
  switch (type) {
    case "always":
      return { always: { value: true } };
    case "user_ids":
      return { user_ids: { user_ids: [] } };
    case "guild_ids":
      return { guild_ids: { guild_ids: [] } };
    case "user_age_range":
      return { user_age_range: {} };
    case "user_id_range":
      return { user_id_range: {} };
    case "guild_id_range":
      return { guild_id_range: {} };
    case "guild_member_count_range":
      return { guild_member_count_range: {} };
    case "user_in_guild":
      return { user_in_guild: { guild_ids: [] } };
    case "user_location":
      return { user_location: { locations: [], prefer_client_ip: false } };
    case "client_location":
      return { client_location: { locations: [] } };
    case "user_ip":
      return { user_ip: { blocks: [], prefer_client_ip: false } };
    case "client_ip":
      return { client_ip: { blocks: [] } };
    case "user_locale":
      return { user_locale: { locales: [] } };
    case "client_locale":
      return { client_locale: { locales: [] } };
    case "user_has_flag":
      return { user_has_flag: { mask: "" } };
    case "staff":
      return { staff: { work_accounts: false, personal_accounts: false } };
    case "bot":
      return { bot: { is_bot: true } };
    case "guild_has_feature":
      return { guild_has_feature: { features: [] } };
    case "installation_ids":
      return { installation_ids: { installation_ids: [] } };
    case "client_release_channel":
      return { client_release_channel: { release_channels: [] } };
    case "unit_id_in_experiment":
      return { unit_id_in_experiment: { experiment_id: "", variation_ids: [] } };
    case "unit_id_in_range_by_hash":
      return { unit_id_in_range_by_hash: { hash_key: "", stop_ring_position: 0, start_ring_position: 0 } };
    case "client_system_locale":
      return { client_system_locale: { locales: [] } };
    case "user_premium_type":
      return { user_premium_type: { premium_types: [] } };
    case "unit_id_matches_filter_snapshot":
      return { unit_id_matches_filter_snapshot: { filter_snapshot_name: "", target_filter_values: [] } };
    case "client_version":
      return {
        client_version: {
          allow_non_native_web: false,
        },
      };
    case "client_os":
      return { client_os: {} };
    default:
      return { always: { value: true } };
  }
}

export function createDefaultRule(): Rule {
  return {
    type: ExperimentType.UNSPECIFIED,
    filters: [],
    is_sunset_rule: false,
    subtype: RuleSubtype.REGULAR,
    hash: "",
  };
}

export function createDefaultBucket(): Bucket {
  return {
    start: 0,
    stop: 100,
    type: ExperimentType.UNSPECIFIED,
  };
}

export function createDefaultVariation(): Variation {
  return {
    id: 0,
    label: "",
    target_allocation: 100,
    buckets: [createDefaultBucket()],
    type: ExperimentType.UNSPECIFIED,
    owning_experiment_id: "",
  };
}

export function createDefaultExperiment(): Experiment {
  return {
    id: "",
    name: "",
    creator_id: "",
    version: 1,
    editor_id: "",
    title: "",
    description: "",
    revision: 1,
    hash_key: "",
    unit_type: ExperimentUnitType.UNSPECIFIED,
    variations: [],
    rules: [],
    phase: Phase.DRAFT,
    surfaces: [],
    owning_team_id: "",
    cached_notification_channel_id: "",
    exposure_tracking: ExposureTracking.ENABLED,
    assignment_mode: AssignmentMode.FULL,
    enable_edit_raw_json_ui: false,
    winning_variation_id: 0,
    extra_outcome_context: "",
    type: ExperimentType.UNSPECIFIED,
    is_template: false,
    field_numbers_to_copy: [],
    engine_feature_flags: [],
    is_automated_change: false,
    suppress_editor_mention: false,
    custom_unit_prefix: 0,
    growthbook_tags: [],
    allocate_right_to_left: false,
    is_managed: false,
    eligibility_persistence: EligibilityPersistence.DEFAULT,
  };
}
